import { Controller } from '@hotwired/stimulus'
import { disableScroll, enableScroll } from '../utils/global'

// Подтверждение отправки формы модалкой в стиле сайта вместо нативного
// confirm(). Вешается на форму: перехватывает submit, подставляет тексты
// в общую модалку #tournament-confirm и отправляет форму только после
// явного «ОК». Тексты приходят через values из ui-переводов; dismiss —
// подпись кнопки отказа, если стандартная «Отмена» звучит двусмысленно
// (например, в вопросе «Отменить запись?»).
//
// Форма может стоять и внутри другой открытой модалки (окно тренировки):
// тогда скролл уже заблокирован ею, а Escape закрывает только подтверждение.
//
// Цель reason — скрытое поле формы для необязательной причины (отмена
// записи): в окне появляется textarea, её текст по «ОК» копируется в поле.
export default class ConfirmFormController extends Controller {
  static targets = ['reason']
  static values = {
    title: String,
    message: String,
    ok: String,
    dismiss: String,
    reasonPlaceholder: String
  }

  confirm(event) {
    if (this.approved) {
      this.approved = false
      return
    }

    const modal = document.getElementById('tournament-confirm')
    if (!modal) return // без модалки в DOM форма работает как обычно

    event.preventDefault()

    modal.querySelector('[data-confirm-title]').textContent = this.titleValue
    modal.querySelector('[data-confirm-message]').textContent =
      this.messageValue
    const okButton = modal.querySelector('[data-confirm-ok]')
    const dismissButton = modal.querySelector('button[data-confirm-cancel]')
    const restoreTexts = [
      this.#setText(okButton, this.okValue),
      this.#setText(dismissButton, this.dismissValue)
    ]

    const reasonField = this.#prepareReason(modal)

    const nested = !!document.querySelector('.modal.is-active')
    const abort = new AbortController()
    const close = () => {
      modal.classList.remove('is-active')
      if (!nested) enableScroll()
      restoreTexts.forEach((restore) => restore())
      this.#hideReason(modal)
      abort.abort()
    }

    okButton.addEventListener(
      'click',
      () => {
        if (reasonField) this.reasonTarget.value = reasonField.value.trim()
        close()
        this.approved = true
        this.element.requestSubmit()
      },
      { signal: abort.signal }
    )
    modal.querySelectorAll('[data-confirm-cancel]').forEach((el) => {
      el.addEventListener('click', close, { signal: abort.signal })
    })
    // Слушатель на document срабатывает раньше window, где Escape ловит
    // окно под подтверждением: останавливаем событие, чтобы оно не закрылось.
    document.addEventListener(
      'keydown',
      (keyEvent) => {
        if (keyEvent.key !== 'Escape') return
        keyEvent.stopPropagation()
        close()
      },
      { signal: abort.signal }
    )

    modal.classList.add('is-active')
    if (!nested) disableScroll()
    okButton.focus()
  }

  // Поле причины показываем только формам с целью reason и каждый раз
  // пустым: окно одно на страницу, текст от прошлой отмены не должен уйти.
  #prepareReason(modal) {
    const box = modal.querySelector('[data-confirm-reason]')
    const field = box?.querySelector('textarea')
    const ask = Boolean(field) && this.hasReasonTarget
    box?.classList.toggle('hidden', !ask)
    if (!ask) return null

    field.value = ''
    field.placeholder = this.reasonPlaceholderValue
    field.dispatchEvent(new Event('input')) // обновить счётчик символов
    return field
  }

  // Модалку открывают и без confirm-form (перенос в coach_calendar):
  // поле причины после закрытия прячем и очищаем, чтобы оно не всплыло там.
  #hideReason(modal) {
    const box = modal.querySelector('[data-confirm-reason]')
    if (!box) return

    box.classList.add('hidden')
    const field = box.querySelector('textarea')
    if (field) field.value = ''
  }

  // Подписи кнопок общие для всех форм страницы: меняем на время показа
  // и возвращаем стандартные при закрытии. Текст кнопки ui-компонента лежит
  // в .btn__text — меняем его, не затирая разметку кнопки.
  #setText(button, text) {
    if (!button || !text) return () => {}

    const label = button.querySelector('.btn__text') || button
    const original = label.textContent
    label.textContent = text
    return () => {
      label.textContent = original
    }
  }
}
